import { createContext, useContext } from 'react'
import { Course, GrowthCategory } from '@mc/api'
import { getModalFeaturedClasses } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import type { SubcatsCoursesOutcomeType } from 'pages/categories/CategoryPage/dataFetching/getSubcatsCoursesOutcomes'
import { CourseOutcome } from 'types/data'
import type { Subcategories, TCourse } from '../../dataFetching'

// data passed in from server-side props
type PageData = {
  slug: string
  categories: GrowthCategory[]
  subcategories: Subcategories
  category: GrowthCategory | null
  courses?: Course[]
  instructors?: string[]
  coursesOutcomes?: Record<string, CourseOutcome[]>
  subcatsCoursesOutcome?: SubcatsCoursesOutcomeType
  quizEntryModalFeaturedClasses?: Awaited<
    ReturnType<typeof getModalFeaturedClasses>
  >
}

// data fetched on the client side, below the fold
type BelowTheFoldData = {
  // TCourse type represents the minimal fields needed to display the instructor tiles.
  // these fields should be shared across courses and camps.
  content: TCourse[]
}

export const PageDataContext = createContext<PageData | null>(null)

type DataProvider<T> = {
  value: T
  children: React.ReactNode
}

export const PageDataProvider = ({
  value,
  children,
}: DataProvider<PageData>) => (
  <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
)

export const usePageData = () => {
  const ctx = useContext(PageDataContext)
  if (!ctx) {
    throw new Error('Did you forget to wrap in PageContextProvider?')
  }
  return ctx
}

const BelowTheFoldDataContext = createContext<BelowTheFoldData | null>(null)

export const BelowTheFoldDataProvider = ({
  value,
  children,
}: DataProvider<BelowTheFoldData>) => (
  <BelowTheFoldDataContext.Provider value={value}>
    {children}
  </BelowTheFoldDataContext.Provider>
)

export const useBelowTheFoldData = () => {
  const ctx = useContext(BelowTheFoldDataContext)
  if (!ctx) {
    throw new Error('Did you forget to wrap in BelowTheFoldDataProvider?')
  }
  return ctx
}
